'use client';

/**
 * `useTurnkeyGasUsage` — operator-facing TanStack Query hook for Turnkey
 * gas-sponsorship usage.
 *
 * Relocated from `frontend/src/queries/turnkey-gas-usage.ts` as part of the
 * auth boundary migration (spec 009 FILE 14 Group E — submission/gas moves
 * into the SDK).
 *
 * Lives in `src/platform/web/auth/` because it depends on `useSdkTurnkey()`,
 * which imports the web-only `@turnkey/react-wallet-kit` vendor SDK. The raw
 * Turnkey `getGasUsage` primitive stays external (allowlist per Group E rule);
 * this hook wraps it under the SDK export boundary so frontend code never
 * touches the Turnkey httpClient directly.
 *
 * Surfaces the same data the submission engine consults during pre-flight,
 * but for human inspection (debug page, admin telemetry view). Not used by
 * the submission path — that path calls Turnkey `getGasUsage` directly via
 * `TurnkeyDeps` so it remains hook-free.
 *
 * The Turnkey organization id is a frontend-owned app config value (it is a
 * singleton across the network toggle). It is injected via the `organizationId`
 * option rather than imported, keeping the hook free of app config.
 */
import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@tanstack/react-query';
import { useSdkTurnkey } from './use-sdk-turnkey.js';
/** Stable query key for the Turnkey gas-usage query. */
export const TURNKEY_GAS_USAGE_QUERY_KEY = 'turnkey-gas-usage';
/**
 * Returns the current Turnkey gas-sponsorship usage for operator surfaces.
 *
 * The query is disabled until the Turnkey httpClient is available (user
 * authenticated). Numbers come back as strings from Turnkey — parsed at this
 * boundary; a parse failure yields `status: 'unknown'`.
 */
export function useTurnkeyGasUsage(t0) {
  const $ = _c(13);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const {
    httpClient
  } = useSdkTurnkey();
  const refetchInterval = options.refetchIntervalMs ?? 60000;
  const organizationId = options.organizationId;
  const t2 = organizationId ?? null;
  const t3 = httpClient === null || httpClient === undefined ? "client-missing" : "client-ready";
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = [TURNKEY_GAS_USAGE_QUERY_KEY, t2, t3];
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const t5 = options.enabled !== false && httpClient !== null && httpClient !== undefined;
  let t6;
  if ($[5] !== httpClient || $[6] !== organizationId) {
    t6 = async () => {
      if (!httpClient) {
        throw new Error("Turnkey httpClient unavailable");
      }
      const client = httpClient;
      const response = await client.getGasUsage(organizationId !== undefined ? {
        organizationId
      } : {});
      const usage = parseFloat(response.usageUsd);
      const limit = parseFloat(response.windowLimitUsd);
      const windowDurationMinutes = response.windowDurationMinutes;
      if (Number.isNaN(usage) || Number.isNaN(limit)) {
        return {
          usageUsd: 0,
          windowLimitUsd: 0,
          windowDurationMinutes,
          remainingUsd: 0,
          pctUsed: 0,
          status: "unknown",
          measuredAt: Date.now()
        };
      }
      const remainingUsd = Math.max(limit - usage, 0);
      const pctUsed = limit > 0 ? usage / limit * 100 : 100;
      const status = limit <= 0 || usage >= limit ? "exhausted" : "available";
      return {
        usageUsd: usage,
        windowLimitUsd: limit,
        windowDurationMinutes,
        remainingUsd,
        pctUsed,
        status,
        measuredAt: Date.now()
      };
    };
    $[5] = httpClient;
    $[6] = organizationId;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== refetchInterval || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      queryKey: t4,
      enabled: t5,
      refetchInterval,
      queryFn: t6
    };
    $[8] = refetchInterval;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return useQuery(t7);
}